import { useTranslation } from 'react-i18next';

import type { EnrichmentPhase } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

interface EnrichmentPhaseBadgeProps {
  /** Adapter/wire phase. Anything but `SAMPLE` renders nothing — `FULL` is what a
   *  reader assumes of a full-catalog row, so only the exception gets a label. */
  phase: EnrichmentPhase | null | undefined;
  className?: string;
}

/** Neutral pill marking a full-catalog row that came out of the sample pass. The
 *  enriched-products read spans both enrichment scopes, and rendering sample output
 *  undistinguished from pipeline output is the ambiguity that made whole-skipped
 *  runs look "zero-enriched" (see `docs/content-enrichment-product-listing.md`).
 *  Origin only — both passes emit the same content for a SKU. */
export function EnrichmentPhaseBadge({ phase, className }: EnrichmentPhaseBadgeProps) {
  const { t } = useTranslation('catalog-operations');
  if (phase !== 'SAMPLE') return null;
  return (
    <span
      data-testid="enrichment-phase-badge"
      title={t('products.samplePassTooltip')}
      className={cn(
        'bg-muted text-muted-foreground inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap',
        className,
      )}
    >
      {t('products.samplePass')}
    </span>
  );
}
